import { useState, useEffect } from "react";
import { toast } from "sonner";
import { FileUp, Loader2, Eye, CheckCircle2, Trash2 } from "lucide-react";
import { useApi, useFetch, errMsg, inr, lastMonth } from "../lib/api";
import { PageHeader, Empty, Pill, VendorMonthBar, useVendors } from "../components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const Confidence = ({ v }) => (
  <Pill tone={v >= 85 ? "green" : v >= 60 ? "amber" : "red"} testid="ocr-confidence-badge">{Number(v || 0).toFixed(0)}% conf</Pill>
);

const VerifyDialog = ({ challan, onClose, onDone }) => {
  const api = useApi();
  const [f, setF] = useState({ trrn_extracted: challan.trrn_extracted || "", total_amount_extracted: challan.total_amount_extracted, headcount_extracted: challan.headcount_extracted });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/challans/${challan.id}/verify`, { ...f, total_amount_extracted: Number(f.total_amount_extracted), headcount_extracted: Number(f.headcount_extracted) });
      toast.success("Challan verified"); onDone(); onClose();
    } catch (er) { toast.error(errMsg(er)); }
  };
  const row = (k, label, type = "text") => (
    <label className="grid gap-1.5"><span className="text-xs text-slate-400">{label}</span>
      <input type={type} step="0.01" className="c-input font-mono" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`challan-verify-${k}`} /></label>
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#121824] border-[#1F2A3E]" data-testid="challan-verify-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Verify {challan.challan_type} challan</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">Check the AI-read values against the document and correct them if needed.</p>
        <form onSubmit={submit} className="grid gap-4">
          {row("trrn_extracted", "TRRN / reference")}
          {row("total_amount_extracted", "Total amount (₹)", "number")}
          {row("headcount_extracted", "Headcount", "number")}
          <Button type="submit" data-testid="challan-verify-submit" className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold">Mark as verified</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Challans() {
  const api = useApi();
  const { data: vendors } = useVendors();
  const { data, reload } = useFetch("/challans");
  const [vendorId, setVendorId] = useState("");
  const [month, setMonth] = useState(lastMonth());
  const [type, setType] = useState("EPF");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verify, setVerify] = useState(null);
  const [drag, setDrag] = useState(false);
  useEffect(() => { if (!vendorId && vendors?.length) setVendorId(vendors[0].id); }, [vendors, vendorId]);

  const upload = async () => {
    const fd = new FormData();
    fd.append("vendor_id", vendorId); fd.append("billing_month", month); fd.append("challan_type", type); fd.append("file", file);
    setBusy(true);
    try {
      const c = (await api.post("/challans", fd)).data;
      toast.success(`AI read ${inr(c.total_amount_extracted)} · ${c.headcount_extracted} members`);
      setFile(null); reload(); setVerify(c);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const view = async (c) => {
    try {
      const r = await api.get(`/challans/${c.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error("No file stored for this challan"); }
  };
  const remove = async (c) => {
    if (!window.confirm("Delete this challan?")) return;
    try { await api.delete(`/challans/${c.id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Proof of payment" title="Challan proofs" desc="Upload EPF or ESIC challans. AI reads the TRRN, amount and headcount; you confirm before it counts toward reconciliation." />
      <VendorMonthBar vendors={vendors} vendorId={vendorId} setVendorId={setVendorId} month={month} setMonth={setMonth}>
        <label className="grid gap-1.5 sm:w-32"><span className="text-xs text-slate-400">Type</span>
          <select className="c-input" value={type} onChange={(e) => setType(e.target.value)} data-testid="challan-type-select"><option>EPF</option><option>ESIC</option></select></label>
      </VendorMonthBar>
      <label
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); setFile(e.dataTransfer.files[0]); }}
        className={`c-surface c-grid-bg flex flex-col items-center justify-center gap-3 p-10 mb-4 cursor-pointer border-dashed transition-colors ${drag ? "!border-[#00E599]" : ""}`}
        data-testid="challan-file-dropzone">
        <FileUp size={28} className="text-[#00E599]" />
        <div className="text-sm">{file ? <span className="font-mono">{file.name}</span> : "Drop a challan PDF or image here, or click to browse"}</div>
        <div className="text-xs text-slate-500">PDF, PNG, JPG, WEBP · up to 10 MB</div>
        <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => { setFile(e.target.files[0]); e.target.value = ""; }} data-testid="challan-file-input" />
      </label>
      <Button onClick={upload} disabled={!file || !vendorId || busy} data-testid="challan-upload-btn" className="mb-8 bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold">
        {busy ? <><Loader2 size={15} className="mr-2 animate-spin" />AI is reading the challan…</> : "Upload & extract"}
      </Button>

      <div className="c-surface overflow-x-auto c-rise">
        <table className="c-table w-full">
          <thead><tr><th>Vendor</th><th>Month</th><th>Type</th><th>TRRN</th><th className="!text-right">Amount</th><th>Headcount</th><th>AI</th><th>Status</th><th /></tr></thead>
          <tbody>
            {(data || []).map((c) => (
              <tr key={c.id} data-testid="challan-table-row">
                <td className="font-medium">{c.vendor_name}<div className="text-[10px] text-slate-500 font-mono truncate max-w-[180px]">{c.original_filename}</div></td>
                <td className="font-mono text-xs">{c.billing_month_year}</td>
                <td><Pill tone="blue">{c.challan_type}</Pill></td>
                <td className="font-mono text-xs">{c.trrn_extracted || "—"}{c.duplicate_trrn && <Pill tone="red">dup</Pill>}</td>
                <td className="font-mono text-right">{inr(c.total_amount_extracted)}</td>
                <td className="font-mono">{c.headcount_extracted}</td>
                <td><Confidence v={c.ocr_confidence_score} /></td>
                <td>{c.is_verified_by_system ? <Pill tone="green" testid="challan-verified-badge">Verified</Pill>
                  : <button onClick={() => setVerify(c)} data-testid="challan-verify-btn" className="text-xs text-amber-400 hover:text-white flex items-center gap-1"><CheckCircle2 size={13} />Verify</button>}</td>
                <td className="text-right whitespace-nowrap">
                  {c.storage_path && <button className="p-1.5 text-slate-400 hover:text-white" onClick={() => view(c)} data-testid="challan-view-btn"><Eye size={14} /></button>}
                  <button className="p-1.5 text-slate-400 hover:text-rose-400" onClick={() => remove(c)} data-testid="challan-delete-btn"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.length === 0 && <Empty text="No challans uploaded yet." />}
      </div>
      {verify && <VerifyDialog challan={verify} onClose={() => setVerify(null)} onDone={reload} />}
    </div>
  );
}
